import { PackageProviderIcon } from 'components/shared/ppoints/PackageProviderIcon';
import { SCard } from 'components/shared/s-polaris';
import { SelectMultiChoicelist } from 'components/shared/select-multi-choicelist/SelectMultiChoicelist';
import { PackageProviderName } from 'data/ppoints/constants';
import { MappedZone } from 'features/wizard/hooks/useZonesWithMappedProviders';
import { ZoneProviderRates } from 'features/wizard/models/shipping-zone-rate-step-form';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PackageProviderService from 'services/settings/ppoints/PackageProviderService';
import { useZoneRateOptions } from './hooks/useZoneRateOptions';

const EMPTY_VALUES: number[] = [];

const toDeliveryProfileGid = (profileId: string) =>
    profileId?.startsWith('gid://') ? profileId : `gid://shopify/DeliveryProfile/${profileId}`;

const ProviderRateRow = ({ zone, provider, values, rateOwners, onChange }: ProviderRateRowProps) => {
    const { t } = useTranslation();
    const [isOpened, setIsOpened] = useState(false);
    const hasRates = Boolean(
        zone.zone.price_based_shipping_rates.length || zone.zone.weight_based_shipping_rates.length
    );

    const rateOptions = useZoneRateOptions(zone, values, isOpened && hasRates);

    const options = useMemo(
        () =>
            rateOptions.options.map((option) => {
                const owner = rateOwners.get(option.value);

                if (!owner || owner === provider) return option;

                return {
                    ...option,
                    label: t('WIZARD.SHIPPING_ZONE_RATE_STEP.RATE_TAKEN_BY', {
                        rate: option.label,
                        provider: PackageProviderService.getDisplayName(owner),
                    }),
                    disabled: true,
                };
            }),
        [rateOptions.options, rateOwners, provider, t]
    );

    return (
        <div className="flex justify-between items-center gap-10">
            <div className="flex gap-2 items-center">
                <PackageProviderIcon provider={provider} size="small" />
                <s-heading>{PackageProviderService.getDisplayName(provider)}</s-heading>
            </div>
            <div className="w-full max-w-100">
                {hasRates ? (
                    <SelectMultiChoicelist
                        options={options}
                        values={values}
                        onChange={onChange}
                        placeholder={t('WIZARD.SHIPPING_ZONE_RATE_STEP.SELECT_RATES_PLACEHOLDER')}
                        selectedOptions={rateOptions.selectedOptions}
                        isResolvingSelection={rateOptions.isResolvingSelection}
                        hasMore={rateOptions.hasMore}
                        isLoadingMore={rateOptions.isLoadingMore}
                        hasPageError={rateOptions.hasPageError}
                        onLoadMore={rateOptions.loadMore}
                        onRetry={rateOptions.retry}
                        onOpen={() => setIsOpened(true)}
                    />
                ) : (
                    <div className="bg-bg-surface-disabled text-text-disabled py-1 px-2 rounded-md">
                        {t('WIZARD.SHIPPING_ZONE_RATE_STEP.NO_RATES_HINT')}
                    </div>
                )}
            </div>
        </div>
    );
};

const RateForProviderSelector: React.FC<RateForProviderSelectorProps> = (props) => {
    const { t } = useTranslation();

    const rateOwners = useMemo(() => {
        const owners = new Map<number, PackageProviderName>();

        Object.entries(props.ratesByProvider ?? {}).forEach(([provider, rateIds]) => {
            (rateIds ?? []).forEach((rateId) => {
                if (owners.has(rateId)) return;

                owners.set(rateId, provider as PackageProviderName);
            });
        });

        return owners;
    }, [props.ratesByProvider]);

    const openDeliveryProfile = async () => {
        props.onLoadingChange?.(true);

        try {
            const activity = await shopify.intents.invoke?.('edit:shopify/DeliveryProfile', {
                value: toDeliveryProfileGid(props.zone.zone.profile_id),
            });

            await activity?.complete;

            await props.onRatesUpdated();
        } finally {
            props.onLoadingChange?.(false);
        }
    };

    return (
        <SCard>
            <s-heading>{t('WIZARD.SHIPPING_ZONE_RATE_STEP.RATES_HEADING', { zone: props.zone.zone.name })}</s-heading>
            <div className="flex flex-col gap-2 py-3">
                {props.zone.providers.map((provider, index) => (
                    <div key={provider}>
                        <ProviderRateRow
                            zone={props.zone}
                            provider={provider}
                            values={props.ratesByProvider[provider] ?? EMPTY_VALUES}
                            rateOwners={rateOwners}
                            onChange={(values) => props.onRatesChange({ ...props.ratesByProvider, [provider]: values })}
                        />
                        {props.zone.providers.length - 1 !== index && (
                            <div className="pt-2">
                                <s-divider />
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div className="flex justify-end">
                <s-button icon="plus" onClick={openDeliveryProfile}>
                    {t('WIZARD.SHIPPING_ZONE_RATE_STEP.ADD_NEW_RATE')}
                </s-button>
            </div>
        </SCard>
    );
};

export default RateForProviderSelector;

interface ProviderRateRowProps {
    zone: MappedZone;
    provider: PackageProviderName;
    values: number[];
    rateOwners: Map<number, PackageProviderName>;
    onChange(values: number[]): void;
}

interface RateForProviderSelectorProps {
    zone: MappedZone;
    ratesByProvider: ZoneProviderRates;
    onRatesChange(ratesByProvider: ZoneProviderRates): void;
    onRatesUpdated(): void | Promise<unknown>;
    onLoadingChange?(loading: boolean): void;
}
